/**
 * XlsxViewerViewEntry — thin wrapper around XlsxViewerView that owns the
 * code-split boundary and the error/suspense fallback UI. This is what the
 * MAIN_VIEWS registry points at; the real component (which statically
 * imports the ~150KB `xlsx` parser) only loads when the user first opens a
 * spreadsheet.
 *
 * The error boundary catches chunk-load failures (network drop mid-import,
 * stale asset after redeploy) and runtime exceptions from SheetJS on a
 * malformed file, showing a recovery UI instead of taking out the main slot.
 *
 * Mirror of DocxEditorViewEntry — kept deliberately parallel so the two
 * preview paths stay consistent.
 */

import { Component, lazy, Suspense, type ReactNode } from 'react';

const XlsxViewerView = lazy(() => import('./XlsxViewerView'));

interface EntryProps {
  path?: string;
  sessionId: string | null;
  onClose: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class XlsxErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error) {
    console.error('[XlsxViewerView] boundary caught:', error);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="xlsx-view xlsx-view--message">
          <div className="xlsx-view-message-title">
            Не удалось открыть таблицу: {this.state.error.message}
          </div>
          <button type="button" className="docx-view-btn" onClick={this.props.onClose}>
            Закрыть
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function XlsxViewerViewEntry(props: EntryProps) {
  // Missing path → registry was called wrong. Fail loud so the call site
  // gets fixed, rather than silently opening an empty view.
  if (!props.path) {
    return (
      <div className="xlsx-view xlsx-view--message">
        <div className="xlsx-view-message-title">XlsxViewerView requires a `path` prop.</div>
      </div>
    );
  }

  return (
    <XlsxErrorBoundary onClose={props.onClose}>
      <Suspense fallback={<div className="xlsx-view xlsx-view--message">Загрузка таблицы…</div>}>
        <XlsxViewerView path={props.path} sessionId={props.sessionId} onClose={props.onClose} />
      </Suspense>
    </XlsxErrorBoundary>
  );
}
